import { Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { trackPageView } from "./utils/analytics";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import HomePage from "./new-pages/home-page/HomePage";
import { LocationProvider } from "./context/NewLocationContext";
import BaseLayout from "./new-components/common/BaseLayout";
import { Backdrop, CircularProgress, Box } from "@mui/material";
import { OrganizationJsonLd } from "./components/common/OrganizationJsonLd";

const MyAccount = lazy(() => import("./new-pages/myAccount/MyAccount"));
const Login = lazy(() => import("./new-pages/auth/Login"));
const MobileVerificationForgot = lazy(() =>
  import("./new-pages/auth/forgotpasscode/MobileVerification").then((m) => ({
    default: m.MobileVerificationForgot,
  })),
);
const MobileVerificationRegistration = lazy(() =>
  import("./new-pages/auth/registration/MobileRegisterVerification").then(
    (m) => ({ default: m.MobileVerificationRegistration }),
  ),
);
const ForgotPasscodeOtpVerification = lazy(() =>
  import("./new-pages/auth/forgotpasscode/OtpVerification").then((m) => ({
    default: m.ForgotPasscodeOtpVerification,
  })),
);
const OtpRegisterVerify = lazy(() =>
  import("./new-pages/auth/registration/OtpRegisterVerify").then((m) => ({
    default: m.OtpRegisterVerify,
  })),
);
const ResetForgotPasscode = lazy(() =>
  import("./new-pages/auth/forgotpasscode/Passcode").then((m) => ({
    default: m.ResetForgotPasscode,
  })),
);
const PasscodeRegistration = lazy(() =>
  import("./new-pages/auth/registration/PasscodeRegistration").then((m) => ({
    default: m.PasscodeRegistration,
  })),
);
const ServiceProviderRegisterDetails = lazy(() =>
  import("./new-pages/auth/serviceProvider/ServiceProviderRegistration").then(
    (m) => ({ default: m.ServiceProviderRegisterDetails }),
  ),
);
const CompleteProfileStep = lazy(
  () => import("./new-pages/auth/registration/CompleteProfileStep"),
);
const ServiceProviderOtpVerify = lazy(() =>
  import("./new-pages/auth/serviceProvider/OtpVerification").then((m) => ({
    default: m.ServiceProviderOtpVerify,
  })),
);
const ServiceProviderSetPasscode = lazy(() =>
  import("./new-pages/auth/serviceProvider/Passcode").then((m) => ({
    default: m.ServiceProviderSetPasscode,
  })),
);
const ServiceProviderRegistrationSuccess = lazy(
  () => import("./new-pages/auth/serviceProvider/ServiceProviderRegistrationSuccess"),
);
const SpEmployeeProfileForm = lazy(
  () => import("./new-pages/auth/sp-employee-registration/SpEmployeeProfileForm"),
);
const SpEmployeeOtpVerify = lazy(() =>
  import("./new-pages/auth/sp-employee-registration/SpEmployeeOtpVerify").then(
    (m) => ({ default: m.SpEmployeeOtpVerify }),
  ),
);
const SpEmployeeRegistrationSuccess = lazy(
  () => import("./new-pages/auth/sp-employee-registration/SpEmployeeRegistrationSuccess"),
);
const SearchResultsPage = lazy(
  () => import("./new-pages/search/SearchResultsPage"),
);
const ManageServiceAreas = lazy(
  () => import("./pages/admin/manage-service-areas/ManageServiceAreas"),
);
const ManageServiceCategories = lazy(
  () =>
    import("./pages/admin/manage-service-categories/ManageServiceCategories"),
);
const ManageCategoryTypes = lazy(
  () =>
    import("./pages/admin/manage-category-types/ManageCategoryTypes"),
);
const ManageServiceKeywords = lazy(
  () => import("./pages/admin/manage-service-keywords/ManageServiceKeyWords"),
);
const ManageServiceAreaLocalities = lazy(
  () =>
    import("./pages/admin/manage-service-area-localities/ManageServiceAreaLocalities"),
);
const ManageContents = lazy(
  () => import("./pages/admin/manage-contents-page/ManageContents"),
);
const AddContents = lazy(
  () => import("./pages/admin/manage-contents-page/AddContents"),
);
const PublicContentPage = lazy(
  () => import("./new-pages/content-pages/PublicContentPage"),
);
const HelpPage = lazy(() => import("./new-pages/help/HelpPage"));
const SuccessfulRegistration = lazy(
  () => import("./new-pages/auth/registration/SuccessfulRegistration"),
);
const SessionTimeout = lazy(
  () => import("./new-pages/auth/SessionTimeout"),
);
const NotFoundPage = lazy(() => import("./new-pages/errors/NotFoundPage"));
const ManageHomeScreenImages = lazy(
  () =>
    import("./new-pages/admin/manage-homescreen-images/ManageHomeScreenImages"),
);
const ManageSettings = lazy(
  () => import("./new-pages/admin/settings/ManageSettings"),
);
const ManageUsers = lazy(
  () => import("./new-pages/admin/manage-users/ManageUsers"),
);
const ManageServiceProviders = lazy(
  () => import("./new-pages/service-providers/ManageServiceProviders"),
);
const DeleteAccount = lazy(
  () => import("./new-components/delete/DeleteAccount"),
);
const SearchUsers = lazy(() => import("./new-pages/users/SearchUsers"));
const ManageFeedbackQuestions = lazy(
  () => import("./new-pages/admin/ManageFeedbackQuestions"),
);
const HelpCenterInbox = lazy(
  () => import("./new-pages/admin/help-center-inbox/HelpCenterInbox"),
);
const PasscodeOtpVerification = lazy(() =>
  import("./new-pages/auth/changePasscode/passCodeComponents/PasscodeOtpVerification").then(
    (m) => ({ default: m.PasscodeOtpVerification }),
  ),
);
const PasscodeReset = lazy(() =>
  import("./new-pages/auth/changePasscode/passCodeComponents/PasscodeReset").then(
    (m) => ({ default: m.PasscodeReset }),
  ),
);
const AllServiceCategories = lazy(
  () => import("./new-pages/service-categories/AllServiceCategories"),
);
const AllCitiesPage = lazy(
  () => import("./new-pages/cities/AllCitiesPage"),
);
const HowItWorksPage = lazy(
  () => import("./new-pages/how-it-works/HowItWorksPage"),
);
const AdminDashboard = lazy(() => import("./new-pages/admin/AdminDashboard"));
const StaffHome = lazy(() => import("./new-pages/admin/StaffHome"));
const AdminLayout = lazy(() => import("./new-components/layouts/AdminLayout"));
const AnalyticsPage = lazy(
  () => import("./new-pages/admin/analytics/AnalyticsPage"),
);
const FavoritesPage = lazy(() => import("./new-pages/favorites/FavoritesPage"));
const UserActivityPage = lazy(
  () => import("./new-pages/myAccount/UserActivityPage"),
);
const ManageChannels = lazy(
  () => import("./new-pages/admin/notifications/ManageChannels"),
);
const ManageProviders = lazy(
  () => import("./new-pages/admin/notifications/ManageProviders"),
);
const ManageTemplates = lazy(
  () => import("./new-pages/admin/notifications/ManageTemplates"),
);
const RegistrationInvite = lazy(
  () => import("./new-pages/admin/notifications/RegistrationInvite"),
);
const AppInfoPage = lazy(
  () => import("./new-components/footer/AppInfoPage"),
);
const LoginRequiredPage = lazy(
  () => import("./new-pages/auth/LoginRequiredPage"),
);

// Sends a GA page_view on every route change
function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

// React Router doesn't reset scroll position on navigation — without this,
// a new page can render mid-scroll (wherever the previous page left off).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Loading fallback component
const LoadingFallback = () => (
  <Backdrop open={true}>
    <CircularProgress color="primary" size={48} />
  </Backdrop>
);

function App() {
  return (
    <LocationProvider>
      <OrganizationJsonLd />
      <Suspense fallback={<LoadingFallback />}>
        <RouteTracker />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<BaseLayout />}>
            <Route path="/content/help" element={<HelpPage />} />
            <Route path="/content/:slug" element={<PublicContentPage />} />
            <Route index element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot" element={<MobileVerificationForgot />} />
            <Route
              path="/register"
              element={<MobileVerificationRegistration />}
            />
            <Route path="/register/verify" element={<OtpRegisterVerify />} />
            <Route
              path="/register/passcode"
              element={<PasscodeRegistration />}
            />
            <Route
              path="/register/complete-profile"
              element={<CompleteProfileStep />}
            />
            <Route
              path="/register/success"
              element={<SuccessfulRegistration />}
            />
            <Route path="/forgot/passcode" element={<ResetForgotPasscode />} />
            <Route
              path="/forgot/verify"
              element={<ForgotPasscodeOtpVerification />}
            />
            <Route path="/session-timeout" element={<SessionTimeout />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            {/* <Route path="/user-registration" element={<UserRegistration />} /> */}
            <Route
              path="/service-provider/register"
              element={<ServiceProviderRegisterDetails />}
            />
            <Route
              path="/service-provider/register/verify-otp"
              element={<ServiceProviderOtpVerify />}
            />
            <Route
              path="/service-provider/register/set-passcode"
              element={<ServiceProviderSetPasscode />}
            />
            <Route
              path="/service-provider/register/success"
              element={<ServiceProviderRegistrationSuccess />}
            />
            <Route path="/account" element={<ProtectedRoute />}>
              <Route index element={<MyAccount />} />
              <Route path="edit" element={<MyAccount />} />
              <Route path="change-passcode" element={<MyAccount />} />
              <Route
                path="change-passcode/otp"
                element={<PasscodeOtpVerification />}
              />
              <Route path="change-passcode/reset" element={<PasscodeReset />} />
              <Route path="settings" element={<MyAccount />} />
              <Route path="favorites" element={<MyAccount />} />
              <Route path="activity" element={<MyAccount />} />
              <Route path="dashboard" element={<MyAccount />} />
              <Route path="help" element={<MyAccount />} />
              <Route path="prices" element={<MyAccount />} />
            </Route>
            <Route
              path="/all-service-categories"
              element={<AllServiceCategories />}
            />
            <Route path="/all-cities" element={<AllCitiesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/favorites" element={<ProtectedRoute />}>
              <Route index element={<FavoritesPage />} />
            </Route>
            <Route path="/user-activity" element={<ProtectedRoute />}>
              <Route index element={<UserActivityPage />} />
            </Route>
            <Route path="/more" element={<Box />} />
            <Route path="/app-info" element={<AppInfoPage />} />
            <Route path="/login-required" element={<LoginRequiredPage />} />
            <Route path="/admin" element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="home" element={<StaffHome />} />
                <Route path="service-areas" element={<ManageServiceAreas />} />
                <Route
                  path="service-categories"
                  element={<ManageServiceCategories />}
                />
                <Route
                  path="category-types"
                  element={<ManageCategoryTypes />}
                />
                <Route path="account-management" element={<SearchUsers />} />
                <Route
                  path="feedback-questions"
                  element={<ManageFeedbackQuestions />}
                />
                <Route
                  path="help-center-inbox"
                  element={<HelpCenterInbox />}
                />
                <Route
                  path="service-keywords"
                  element={<ManageServiceKeywords />}
                />
                <Route
                  path="service-area-localities"
                  element={<ManageServiceAreaLocalities />}
                />
                <Route
                  path="service-providers"
                  element={<ManageServiceProviders />}
                />
                <Route
                  path="home-screen-images"
                  element={<ManageHomeScreenImages />}
                />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="settings" element={<ManageSettings />} />
                <Route path="users" element={<ManageUsers />} />
                <Route path="content-pages" element={<ManageContents />} />
                {/* SP_EMPLOYEE registration flow */}
                <Route
                  path="sp-registration"
                  element={<SpEmployeeProfileForm />}
                />
                <Route
                  path="sp-registration/verify-otp"
                  element={<SpEmployeeOtpVerify />}
                />
                <Route
                  path="sp-registration/success"
                  element={<SpEmployeeRegistrationSuccess />}
                />
                {/* Notification Management Routes */}
                <Route
                  path="notification-channels"
                  element={<ManageChannels />}
                />
                <Route
                  path="notification-providers"
                  element={<ManageProviders />}
                />
                <Route
                  path="notification-templates"
                  element={<ManageTemplates />}
                />
                <Route
                  path="registration-invite"
                  element={<RegistrationInvite />}
                />

                {/* Routes that should NOT have the admin tabs */}
                <Route path="content-pages/add" element={<AddContents />} />
                <Route
                  path="content-pages/edit/:id"
                  element={<AddContents />}
                />
                <Route
                  path="content-pages/view/:id"
                  element={<AddContents />}
                />
              </Route>
            </Route>
            <Route path="/field-operator" element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route
                  path="dashboard"
                  element={<AdminDashboard />}
                />
                <Route path="home" element={<StaffHome />} />
                <Route
                  path="service-providers"
                  element={<ManageServiceProviders />}
                />
                <Route path="account-management" element={<SearchUsers />} />
                <Route
                  path="registration-invite"
                  element={<RegistrationInvite />}
                />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route
                  path="help-center-inbox"
                  element={<HelpCenterInbox />}
                />
              </Route>
              {/* SP_EMPLOYEE registration flow */}
              <Route
                path="sp-registration"
                element={<SpEmployeeProfileForm />}
              />
              <Route
                path="sp-registration/verify-otp"
                element={<SpEmployeeOtpVerify />}
              />
              <Route
                path="sp-registration/success"
                element={<SpEmployeeRegistrationSuccess />}
              />
            </Route>
            <Route path="/:location" element={<SearchResultsPage />} />
            <Route
              path="/:location/:keyword"
              element={<SearchResultsPage />}
            />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </LocationProvider>
  );
}

export default App;
